import { useState, useMemo, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";
import { useTranslation } from "react-i18next";
import UiButton from "../../../../components/button/Button";
import { useTags } from "./useTags";
import { useParticipantTags } from "./useParticipantTags";
import { EndpointsService } from "../../../../services/endpoints/endpoints.service";
import UiFieldMessage from "../../../../components/field-message/FieldMessage.component";
import { FieldMessageSeverity } from "../../../../models/components/field-message.model";

const TagsEditModal = ({
	participantId,
	closeModal,
	isVisible,
}: {
	participantId: number;
	closeModal: () => void;
	isVisible: boolean;
}) => {
	const { t } = useTranslation("common");

	const allTags = useTags();
	const participantTags = useParticipantTags({ patientId: participantId });

	// Track local checkbox state: Set of tag IDs that are checked
	const [checkedTagIds, setCheckedTagIds] = useState<Set<number>>(() => {
		return new Set(participantTags.data?.map((tag) => tag.id) ?? []);
	});
	const [hasSeededFromData, setHasSeededFromData] = useState(!!participantTags.data);

	// The initializer above only runs once, at mount. If the participant's
	// current tags haven't loaded yet when the modal opens (react-query cache
	// miss), that initial Set is empty and would never be corrected, so every
	// checkbox would silently start unchecked even for already-assigned tags.
	// Re-seed exactly once, the first time the data becomes available.
	useEffect(() => {
		if (!hasSeededFromData && participantTags.data) {
			setCheckedTagIds(new Set(participantTags.data.map((tag) => tag.id)));
			setHasSeededFromData(true);
		}
	}, [hasSeededFromData, participantTags.data]);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [hasSaveError, setHasSaveError] = useState(false);

	const handleCheckboxChange = (event: CheckboxChangeEvent, tagId: number) => {
		const newCheckedIds = new Set(checkedTagIds);
		if (event.checked) {
			newCheckedIds.add(tagId);
		} else {
			newCheckedIds.delete(tagId);
		}
		setCheckedTagIds(newCheckedIds);
	};

	const handleSave = async () => {
		setIsSubmitting(true);
		setHasSaveError(false);
		try {
			await EndpointsService.dataRetriever.updateParticipantTags({
				participantId,
				body: {
					tagIds: Array.from(checkedTagIds),
				},
			});
			closeModal();
		} catch (error) {
			// A failed save must stay on screen and say so. Logging to the console
			// only, as this did, made a 500 look exactly like a successful save that
			// changed nothing -- the modal simply sat there. That is how the KAN-2340
			// backend bug reached testing undiagnosed.
			setHasSaveError(true);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCancel = () => {
		closeModal();
	};

	const sortedTags = useMemo(() => {
		return (allTags.data ?? []).sort((a, b) => a.sortOrder - b.sortOrder);
	}, [allTags.data]);

	return (
		<Dialog
			header={
				<div className="dialog-custom-header">
					<span className="dialog-custom-header__title">
						{t("PARTICIPANT.DASHBOARD.TAGS.MODAL.TITLE")}
					</span>
				</div>
			}
			visible={isVisible}
			breakpoints={{ "960px": "80vw", "641px": "100vw" }}
			style={{ width: "80vw" }}
			modal={true}
			focusOnShow={false}
			onHide={closeModal}
		>
			<div className="tags-edit-modal-content">
				{allTags.isLoading ? (
					<p>{t("PARTICIPANT.DASHBOARD.TAGS.MESSAGES.LOADING")}</p>
				) : allTags.isError ? (
					<p>{t("PARTICIPANT.DASHBOARD.TAGS.MESSAGES.ERROR")}</p>
				) : (
					<div
						className="tags-list"
						style={{
							maxHeight: "500px",
							overflowY: "auto",
							border: "1px solid #E5E7EB",
							borderRadius: "4px",
							padding: "12px",
						}}
					>
						{sortedTags.map((tag) => (
							<div
								key={tag.id}
								className="tag-checkbox-item"
								style={{
									display: "flex",
									alignItems: "center",
									padding: "8px 0",
									borderBottom: "1px solid #F3F4F6",
								}}
							>
								<Checkbox
									inputId={`tag-${tag.id}`}
									checked={checkedTagIds.has(tag.id)}
									onChange={(e) => handleCheckboxChange(e, tag.id)}
									disabled={isSubmitting}
									style={{ marginRight: "8px" }}
								/>
								<span
									className="tag-dot"
									style={{
										backgroundColor: tag.color,
										display: "inline-block",
										width: "8px",
										height: "8px",
										borderRadius: "50%",
										marginRight: "8px",
										verticalAlign: "middle",
									}}
								></span>
								<label
									htmlFor={`tag-${tag.id}`}
									style={{
										cursor: "pointer",
										flex: 1,
										margin: 0,
									}}
								>
									{tag.name}
									{tag.category && (
										<span
											style={{
												marginLeft: "6px",
												fontSize: "0.85em",
												color: "#6B7280",
												fontWeight: "normal",
											}}
										>
											· {tag.category}
										</span>
									)}
								</label>
							</div>
						))}
					</div>
				)}

				{hasSaveError && (
					<div className="form-message">
						<UiFieldMessage
							severity={FieldMessageSeverity.DANGER}
							label="PARTICIPANT.DASHBOARD.TAGS.MESSAGES.ERROR_SAVE"
						/>
					</div>
				)}

				<div className="action-buttons">
					<UiButton
						label="UI_COMPONENTS.BUTTONS.CANCEL"
						type="button"
						className="p-button-outlined p-button-rounded"
						onClick={handleCancel}
						disabled={isSubmitting}
					/>
					<UiButton
						label={
							isSubmitting
								? "UI_COMPONENTS.BUTTONS.SAVING"
								: "UI_COMPONENTS.BUTTONS.SAVE"
						}
						type="button"
						className="p-button-rounded"
						onClick={handleSave}
						loading={isSubmitting}
						disabled={isSubmitting}
					/>
				</div>
			</div>
		</Dialog>
	);
};

export default TagsEditModal;
